/**
 * The learner's own agent, at `/agent` (components/agent/AgentPage.tsx,
 * docs/agents.md): the address the page lives at, and the settings it hands
 * the learner beside their agent key.
 *
 * The page was the "Use your own agent" section of the paid task board until
 * 2026-10-31, when the owner retired paid tasks and AI credits. What a learner
 * still needs from it, the MCP server and the keys, moved here; the old
 * addresses redirect (lib/retired-tasks.ts).
 */

/** Where the agent page lives. Every "make a key" link in the app points here. */
export const AGENT_PATH = '/agent';

/** Whether this address is the agent page; a trailing slash is the same page. */
export function isAgentPath(pathname: string): boolean {
  return /^\/agent\/?$/.test(pathname);
}

/** Where the public site lives, when the page cannot say (a test, a server render). */
export const AI_FALLBACK_ORIGIN = 'https://engineeros.dev';

/** Where Claude Code's OpenTelemetry export is sent (worker/ai-telemetry.ts). */
export const AI_REPORT_PATH = '/api/ai/otel';

/**
 * The `.claude/settings.json` that has Claude Code report its own model calls
 * to us (docs/efficiency-meter.md, "Reported calls"), for a learner running it
 * on their own plan through the MCP server. Logs only, as JSON: the endpoint
 * reads `api_request` records and refuses protobuf. The key is the agent key
 * the MCP server takes, so one key names the learner for both.
 */
export function reportSettings(origin: string, agentKey: string): string {
  const base = /^https?:\/\/[^\s"]+$/.test(origin.trim()) ? origin.trim().replace(/\/+$/, '') : AI_FALLBACK_ORIGIN;
  return JSON.stringify({
    env: {
      CLAUDE_CODE_ENABLE_TELEMETRY: '1',
      OTEL_LOGS_EXPORTER: 'otlp',
      OTEL_EXPORTER_OTLP_PROTOCOL: 'http/json',
      OTEL_EXPORTER_OTLP_ENDPOINT: `${base}${AI_REPORT_PATH}`,
      OTEL_EXPORTER_OTLP_HEADERS: `Authorization=Bearer ${agentKey}`,
      OTEL_LOGS_EXPORT_INTERVAL: '5000',
    },
  }, null, 2);
}
